import { memo, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CheckCircleIcon, ExclamationIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { ModalWrapper } from '@/components';
import { CustomButton } from '@/components/common';

const AspynResultModal = ({
  isOpened,
  onClose,
  result,
  modalWidth = 'max-w-[650px] w-full',
}) => {
  // Extract data from the response structure: data.report as an array of steps
  const reportSteps = useMemo(() => result?.data?.report || [], [result]);

  // State to track which entities are expanded
  const [expandedEntities, setExpandedEntities] = useState({});

  // Group steps by entity while preserving original step numbers
  const groupedSteps = useMemo(() => {
    const groups = {};
    reportSteps.forEach((step, originalIndex) => {
      const entityName = step.entity || 'Other';
      if (!groups[entityName]) {
        groups[entityName] = [];
      }
      groups[entityName].push({
        ...step,
        originalStepNumber: originalIndex + 1,
      });
    });

    return groups;
  }, [reportSteps]);

  // Toggle entity expansion
  const toggleEntity = (entityName) => {
    setExpandedEntities((prev) => ({
      ...prev,
      [entityName]: !prev[entityName],
    }));
  };

  // Determine overall success based on all steps
  const allStepsSuccessful = reportSteps.length > 0 && reportSteps.every((step) => step.is_successful);
  const hasAnyFailure = reportSteps.some((step) => !step.is_successful);

  const isSuccessful = allStepsSuccessful && !hasAnyFailure;
  const iconBgClass = isSuccessful ? 'bg-green-100' : 'bg-aptivered-lightest';
  const iconClass = isSuccessful ? 'stroke-green-600' : 'stroke-aptivered';
  const titleText = isSuccessful ? 'Push to Aspyn Successful' : 'Push to Aspyn Results';
  const buttonColor = isSuccessful ? 'green' : 'red';

  // Find the entity containing the failed step with the smallest step number
  const firstFailedEntity = useMemo(() => {
    let minStepNumber = Infinity;
    let entityWithMinFailedStep = null;

    for (const [entityName, steps] of Object.entries(groupedSteps)) {
      const failedSteps = steps.filter((step) => !step.is_successful);
      if (failedSteps.length > 0) {
        const minInThisEntity = Math.min(...failedSteps.map((step) => step.originalStepNumber));
        if (minInThisEntity < minStepNumber) {
          minStepNumber = minInThisEntity;
          entityWithMinFailedStep = entityName;
        }
      }
    }
    return entityWithMinFailedStep;
  }, [groupedSteps]);

  // Expand first failed step
  const expandFirstFailed = () => {
    if (firstFailedEntity) {
      setExpandedEntities((prev) => ({
        ...prev,
        [firstFailedEntity]: true,
      }));
    }
  };

  return (
    <ModalWrapper
      onCloseModal={onClose}
      isOpened={isOpened}
      width={modalWidth}
    >
      <div className="p-6 flex flex-nowrap items-start justify-between gap-x-4">
        <div className="w-10">
          <div className={`w-10 h-10 flex items-center justify-center ${iconBgClass} rounded-full`}>
            {isSuccessful ? (
              <CheckCircleIcon className={`h-6 w-6 ${iconClass}`} aria-hidden="true" />
            ) : (
              <ExclamationIcon className={`h-6 w-6 ${iconClass}`} aria-hidden="true" />
            )}
          </div>
        </div>
        <div className="grow">
          <h1 className="mb-4 text-lg font-medium text-gray-900">{titleText}</h1>

          {firstFailedEntity && !expandedEntities[firstFailedEntity] && (
            <div className="mb-4">
              <CustomButton
                color="secondary"
                onClick={expandFirstFailed}
                className="px-4 py-2 font-normal text-sm"
              >
                Show First Failed Step
              </CustomButton>
            </div>
          )}

          {reportSteps.length > 0 ? (
            <div className="mb-4 space-y-2">
              {Object.entries(groupedSteps).map(([entityName, steps]) => {
                const isExpanded = expandedEntities[entityName];
                const hasFailures = steps.some((step) => !step.is_successful);
                const allSuccess = steps.every((step) => step.is_successful);

                return (
                  <div key={entityName} className="border border-gray-200 rounded-md overflow-hidden">
                    {/* Entity Header - Clickable */}
                    <button
                      type="button"
                      onClick={() => toggleEntity(entityName)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-150 text-left"
                    >
                      <div className="flex items-center gap-x-3">
                        <div className="flex-shrink-0">
                          {allSuccess ? (
                            <CheckCircleIcon className="h-5 w-5 stroke-green-600" aria-hidden="true" />
                          ) : hasFailures ? (
                            <ExclamationIcon className="h-5 w-5 stroke-red-600" aria-hidden="true" />
                          ) : (
                            <CheckCircleIcon className="h-5 w-5 stroke-gray-400" aria-hidden="true" />
                          )}
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">{entityName}</span>
                          <span className="ml-2 text-xs text-gray-500">({steps.length} step{steps.length !== 1 ? 's' : ''})</span>
                        </div>
                      </div>
                      <ChevronDownIcon
                        className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>

                    {/* Collapsible Steps */}
                    <div
                      className={`grid transition-all duration-300 ease-in-out ${
                        isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="p-3 space-y-2 bg-white">
                          {steps.map((step, index) => (
                            <div key={index} className="flex items-start gap-x-3 p-3 border border-gray-200 rounded-md bg-gray-50">
                              <div className="flex-shrink-0">
                                {step.is_successful ? (
                                  <CheckCircleIcon className="h-5 w-5 stroke-green-600" aria-hidden="true" />
                                ) : (
                                  <ExclamationIcon className="h-5 w-5 stroke-red-600" aria-hidden="true" />
                                )}
                              </div>
                              <div className="flex-grow">
                                <div className={`text-sm font-medium ${step.is_successful ? 'text-green-800' : 'text-red-800'}`}>
                                  Step {step.originalStepNumber}: {step.is_successful ? 'Success' : 'Failed'}
                                </div>
                                <div
                                  className="text-sm text-gray-600 mt-1 cursor-help"
                                  title={step.message || 'No message provided'}
                                >
                                  {step.message || 'No message provided'}
                                </div>
                                {step.context && step.context.map((contextItem, ctxIndex) => (
                                  <div
                                    key={ctxIndex}
                                    className="text-sm text-gray-600 mt-1 cursor-help"
                                    title={contextItem}
                                  >
                                    {contextItem}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mb-4 text-sm text-gray-600">No steps to display</p>
          )}

          <div className="flex items-center justify-end">
            <CustomButton
              color={buttonColor}
              onClick={onClose}
              className="px-6 py-2 font-normal"
            >
              Close
            </CustomButton>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};

AspynResultModal.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  result: PropTypes.shape({
    data: PropTypes.shape({
      report: PropTypes.arrayOf(PropTypes.shape({
        is_successful: PropTypes.bool,
        message: PropTypes.string,
        context: PropTypes.arrayOf(PropTypes.string),
        entity: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })),
    }),
  }),
  modalWidth: PropTypes.string,
};

export default memo(AspynResultModal);
